/**
 * Editor state for the planner.
 *
 * Undo/redo is built in rather than added later because route planning is an
 * inherently fiddly, mistake-prone activity: you will drag a waypoint onto the
 * wrong road, and having to reconstruct it by hand is the difference between a
 * tool you enjoy and one you tolerate.
 *
 * History granularity is deliberate. A marker drag fires continuously, but only
 * the drag's end is a history entry — otherwise one drag would take forty
 * undos to reverse.
 */

import { useCallback, useMemo, useReducer } from 'react';
import { emptyDraft, newWaypointId, type DraftRoute, type RouteOptions, type RoutingProfile, type Waypoint } from '@cad/core';

const HISTORY_LIMIT = 100;

export interface EditorState {
  draft: DraftRoute;
  past: DraftRoute[];
  future: DraftRoute[];
  selectedWaypointId: string | null;
  /** Set once the route has been saved; drives PUT-vs-POST and the share link. */
  savedId: string | null;
  savedRevision: number;
  /** True when the draft differs from what was last saved. */
  dirty: boolean;
}

export type EditorAction =
  | { type: 'add-waypoint'; lng: number; lat: number; index?: number }
  | { type: 'move-waypoint'; id: string; lng: number; lat: number; commit: boolean }
  | { type: 'remove-waypoint'; id: string }
  | { type: 'reorder-waypoints'; from: number; to: number }
  | { type: 'set-waypoint-kind'; id: string; kind: Waypoint['kind'] }
  | { type: 'rename-waypoint'; id: string; name: string }
  | { type: 'select-waypoint'; id: string | null }
  | { type: 'set-name'; name: string }
  | { type: 'set-notes'; notes: string }
  | { type: 'set-options'; options: Partial<RouteOptions> }
  | { type: 'set-profile'; profile: RoutingProfile }
  | { type: 'reverse' }
  | { type: 'clear' }
  | { type: 'load'; draft: DraftRoute; id: string | null; revision: number }
  | { type: 'mark-saved'; id: string; revision: number }
  | { type: 'undo' }
  | { type: 'redo' };

export function initialEditorState(): EditorState {
  return {
    draft: emptyDraft(),
    past: [],
    future: [],
    selectedWaypointId: null,
    savedId: null,
    savedRevision: 0,
    dirty: false,
  };
}

/** Apply a change and push the previous draft onto the undo stack. */
function commit(state: EditorState, next: DraftRoute): EditorState {
  return {
    ...state,
    draft: next,
    past: [...state.past, state.draft].slice(-HISTORY_LIMIT),
    future: [],
    dirty: true,
  };
}

/** Apply a change without touching history — for the middle of a drag. */
function transient(state: EditorState, next: DraftRoute): EditorState {
  return { ...state, draft: next, dirty: true };
}

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  const { draft } = state;

  switch (action.type) {
    case 'add-waypoint': {
      const waypoint: Waypoint = {
        id: newWaypointId(),
        lng: action.lng,
        lat: action.lat,
        // First and last points are the ones you actually arrive at; everything
        // dropped in between is assumed to be shaping the line until told otherwise.
        kind: draft.waypoints.length === 0 ? 'stop' : 'via',
      };
      const waypoints = [...draft.waypoints];
      const at = action.index ?? waypoints.length;
      waypoints.splice(at, 0, waypoint);

      // The end of the route is always somewhere you arrive.
      const normalised = waypoints.map((w, i) =>
        i === waypoints.length - 1 && w.kind === 'via' ? { ...w, kind: 'stop' as const } : w,
      );
      return { ...commit(state, { ...draft, waypoints: normalised }), selectedWaypointId: waypoint.id };
    }

    case 'move-waypoint': {
      const waypoints = draft.waypoints.map((w) =>
        w.id === action.id ? { ...w, lng: action.lng, lat: action.lat } : w,
      );
      const next = { ...draft, waypoints };
      return action.commit ? commit(state, next) : transient(state, next);
    }

    case 'remove-waypoint': {
      const waypoints = draft.waypoints.filter((w) => w.id !== action.id);
      return {
        ...commit(state, { ...draft, waypoints }),
        selectedWaypointId: state.selectedWaypointId === action.id ? null : state.selectedWaypointId,
      };
    }

    case 'reorder-waypoints': {
      if (action.from === action.to) return state;
      const waypoints = [...draft.waypoints];
      const [moved] = waypoints.splice(action.from, 1);
      if (!moved) return state;
      waypoints.splice(action.to, 0, moved);
      return commit(state, { ...draft, waypoints });
    }

    case 'set-waypoint-kind':
      return commit(state, {
        ...draft,
        waypoints: draft.waypoints.map((w) => (w.id === action.id ? { ...w, kind: action.kind } : w)),
      });

    case 'rename-waypoint':
      return commit(state, {
        ...draft,
        waypoints: draft.waypoints.map((w) =>
          w.id === action.id ? { ...w, ...(action.name ? { name: action.name } : { name: undefined }) } : w,
        ),
      });

    case 'select-waypoint':
      return { ...state, selectedWaypointId: action.id };

    case 'set-name':
      return { ...state, draft: { ...draft, name: action.name }, dirty: true };

    case 'set-notes':
      return { ...state, draft: { ...draft, notes: action.notes }, dirty: true };

    case 'set-options':
      return commit(state, { ...draft, options: { ...draft.options, ...action.options } });

    case 'set-profile':
      return commit(state, { ...draft, profile: action.profile });

    case 'reverse':
      return commit(state, { ...draft, waypoints: [...draft.waypoints].reverse() });

    case 'clear':
      return { ...commit(state, { ...draft, waypoints: [] }), selectedWaypointId: null };

    case 'load':
      return {
        draft: action.draft,
        past: [],
        future: [],
        selectedWaypointId: null,
        savedId: action.id,
        savedRevision: action.revision,
        dirty: false,
      };

    case 'mark-saved':
      return { ...state, savedId: action.id, savedRevision: action.revision, dirty: false };

    case 'undo': {
      const previous = state.past[state.past.length - 1];
      if (!previous) return state;
      return {
        ...state,
        draft: previous,
        past: state.past.slice(0, -1),
        future: [state.draft, ...state.future].slice(0, HISTORY_LIMIT),
        dirty: true,
      };
    }

    case 'redo': {
      const next = state.future[0];
      if (!next) return state;
      return {
        ...state,
        draft: next,
        past: [...state.past, state.draft].slice(-HISTORY_LIMIT),
        future: state.future.slice(1),
        dirty: true,
      };
    }

    default:
      return state;
  }
}

export function useRouteEditor() {
  const [state, dispatch] = useReducer(editorReducer, undefined, initialEditorState);

  const canUndo = state.past.length > 0;
  const canRedo = state.future.length > 0;

  const undo = useCallback(() => dispatch({ type: 'undo' }), []);
  const redo = useCallback(() => dispatch({ type: 'redo' }), []);

  /**
   * Waypoints are compared by position and order only. Renaming a stop or
   * flipping stop/via changes the itinerary but not the line, so it must not
   * trigger a re-route — that would burn requests on every keystroke.
   */
  const routingKey = useMemo(
    () =>
      JSON.stringify([
        state.draft.waypoints.map((w) => [w.lng.toFixed(6), w.lat.toFixed(6), w.kind]),
        state.draft.profile,
        state.draft.options,
      ]),
    [state.draft.waypoints, state.draft.profile, state.draft.options],
  );

  return { state, dispatch, canUndo, canRedo, undo, redo, routingKey };
}
